/**
 * App shell / ops cache on TotalAccessOfflineDB (`app-shell` domain) — Smart Task & operations KV snapshots.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

const DOMAIN = 'app-shell' as const;
const STORE = 'kv' as const;

function getDb() {
  return offlineDb;
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T>(
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
): Promise<OfflineDomainRecord> {
  const now = new Date().toISOString();
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store: STORE,
    client_id: clientId,
    entity_id: entityId,
    sync_status: 'synced',
    created_at: existing?.created_at || now,
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

async function getStoreRow(clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }
  return db.records.where('[domain+store+client_id+entity_id]').equals([DOMAIN, STORE, clientId, entityId]).first();
}

const appShellStore = {
  async upsertJson<T>(clientId: number, entityId: string, value: T): Promise<void> {
    const db = getDb();
    if (!db || !clientId || !entityId) {
      return;
    }

    const existing = await getStoreRow(clientId, entityId);
    const row = await buildRecordRow(clientId, entityId, value, existing);
    await db.records.put(row);
  },

  async getJsonUnifiedOnly<T>(clientId: number, entityId: string): Promise<T | null> {
    const row = await getStoreRow(clientId, entityId);
    const decrypted = await decryptRecordPayload<T>(row);
    if (decrypted === undefined || decrypted === null) {
      return null;
    }
    return decrypted as T;
  },

  /** Read JSON blob from offline DB only. */
  async getJson<T>(clientId: number, entityId: string): Promise<T | null> {
    return this.getJsonUnifiedOnly<T>(clientId, entityId);
  },

  /** Array payloads use unified shape `{ items: T[] }`. */
  async loadSettingsArrayMigration<T>(clientId: number, entityId: string): Promise<T[]> {
    const boxed = await this.getJsonUnifiedOnly<{ items?: T[] }>(clientId, entityId);
    if (boxed && Array.isArray(boxed.items)) {
      return boxed.items as T[];
    }
    return [];
  },

  async saveSettingsArrayMigration<T>(clientId: number, entityId: string, items: T[]): Promise<void> {
    await this.upsertJson(clientId, entityId, { items });
  },

  async deleteJson(clientId: number, entityId: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await getStoreRow(clientId, entityId);
    if (row?.id !== undefined && typeof row.id === 'number') {
      await db.records.delete(row.id);
    }
  },

  async clearClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }

    const rows = await db.records.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray();
    const ids = rows.map((row) => row.id).filter((id): id is number => typeof id === 'number');

    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },
};

export default appShellStore;
